import { cardUpdateChangesContent, invalidateDeckCardRequests } from "../utils/deck-card-requests";
import { cardStore } from "../../zustand/globalStore";
import {
  collection,
  deleteField,
  getDocs,
  setDoc,
  doc,
  query,
  orderBy,
  getDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../utils/init-firebase";
import { arrayElementLooper, trackEvent } from "../utils/helper-methods";
import {
  CARDS_SAVED_COLLECTION_NAME,
  USERS_COLLECTION_NAME,
  DECK_COLLECTION_NAME,
  JUNGLE_BACKEND_PROD_DOMAIN,
  CardData,
  UserData,
  CardType,
  CARDS_DELETED_COLLECTION_NAME,
  JUNGLE_CLOUDFLARE_BASE_URL,
  AssessmentUnitStimulusBlock,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { removeAnkiCard } from "../AnkiAPIMethods";
import axios from "axios";
import { getQuestionVersionToDisplay } from "../utils/rephrasing-helpers";
import { trackError } from "../../utils/analytics/track-error";
import { buildFirestoreAssessmentUnitStimulus } from "../../utils/assessment-units";

export const rephraseQuestionAndAnswer = async (
  cardId: string,
  originalQuestion: string,
  originalAnswer: string,
  relevantContextForQuestion: string,
  distractorAnswers?: string[] // only for MCQ
): Promise<{
  rephrased_question: string;
  rephrased_answer: string;
  rephrased_distractor_answers: string[];
} | null> => {
  try {
    const endpoint = `${JUNGLE_BACKEND_PROD_DOMAIN}/cards/rephrase_question_and_answer`;

    // Validate inputs before sending
    if (!cardId || !originalQuestion || !originalAnswer) {
      console.error("Missing required fields for rephrasing");
      return null;
    }

    const requestData = {
      card_id: cardId,
      original_question: originalQuestion,
      original_answer: originalAnswer,
      distractor_answers: distractorAnswers,
      relevant_context_for_question: relevantContextForQuestion || "",
    };

    const { data } = await axios.post(endpoint, requestData, {
      headers: { "Content-Type": "application/json" },
    });

    if (data.status === "success") {
      return {
        rephrased_question: data.rephrased_question,
        rephrased_answer: data.rephrased_answer,
        rephrased_distractor_answers: data.rephrased_distractor_answers,
      };
    } else {
      console.error("Failed to rephrase question:", data.error);
      return null;
    }
  } catch (error: any) {
    if (error.response) {
      console.error("Error rephrasing question - Status:", error.response.status);
      console.error("Error rephrasing question - Data:", error.response.data);
    } else {
      console.error("Error rephrasing question:", error);
    }
    return null;
  }
};

export const getCardById = async (cardId: string): Promise<CardData | null> => {
  const cardDocRef = doc(db, CARDS_SAVED_COLLECTION_NAME, cardId);
  const cardDoc = await getDoc(cardDocRef);
  if (cardDoc.exists()) {
    return { id: cardDoc.id, ...cardDoc.data() } as CardData;
  } else {
    console.error("No such document!");
    return null;
  }
};

export const getDeletedCardById = async (cardId: string): Promise<CardData | null> => {
  const cardDocRef = doc(db, CARDS_DELETED_COLLECTION_NAME, cardId);
  const cardDoc = await getDoc(cardDocRef);
  if (cardDoc.exists()) {
    return { id: cardDoc.id, ...cardDoc.data() } as CardData;
  } else {
    console.error("No such document!");
    return null;
  }
};

export const updateCardById = async (cardId: string, fieldsToUpdate: any): Promise<boolean> => {
  const contentChanged = cardUpdateChangesContent(fieldsToUpdate);
  const state = cardStore.getState();
  const card = state.cardsData?.find(card => card.id === cardId) ??
    state.allCardsForCurrentDeckAndSubdecks?.find(card => card.id === cardId);
  if (contentChanged) invalidateDeckCardRequests(card?.deck_id);
  try {
    await setDoc(doc(db, `${CARDS_SAVED_COLLECTION_NAME}`, cardId), fieldsToUpdate, {
      merge: true,
    });
    return true;
  } catch (err) {
    console.error("Error updating card: ", err);
    return false;
  } finally {
    if (contentChanged) invalidateDeckCardRequests(card?.deck_id);
  }
};

export const updateAssessmentUnitSharedStimulus = async (
  assessmentUnitId: string,
  stimulusBlocks: AssessmentUnitStimulusBlock[],
  passageFallbackText: string,
  editedCardId: string,
  editedCardFields: Record<string, unknown>
) => {
  if (!assessmentUnitId || stimulusBlocks.length === 0 || !passageFallbackText.trim()) {
    throw new Error("A shared assessment unit and non-empty passage are required.");
  }

  const siblingCardsSnapshot = await getDocs(
    query(collection(db, CARDS_SAVED_COLLECTION_NAME), where("assessment_unit_id", "==", assessmentUnitId))
  );
  if (siblingCardsSnapshot.empty) {
    throw new Error(`No cards found for assessment unit ${assessmentUnitId}.`);
  }
  if (!siblingCardsSnapshot.docs.some((siblingCardDocument) => siblingCardDocument.id === editedCardId)) {
    throw new Error(`Card ${editedCardId} does not belong to assessment unit ${assessmentUnitId}.`);
  }

  const firestoreStimulus = buildFirestoreAssessmentUnitStimulus(stimulusBlocks);
  const batch = writeBatch(db);
  siblingCardsSnapshot.docs.forEach((siblingCardDocument) => {
    batch.update(siblingCardDocument.ref, {
      ...(siblingCardDocument.id === editedCardId ? editedCardFields : {}),
      case_scenario_details: passageFallbackText,
      assessment_unit_stimulus_schema_version: firestoreStimulus.schemaVersion,
      assessment_unit_stimulus: firestoreStimulus.blocks,
      assessment_unit_stimulus_override_markdown: deleteField(),
    });
  });
  invalidateDeckCardRequests();
  try {
    await batch.commit();
  } finally {
    invalidateDeckCardRequests();
  }
};

//
export const deleteSavedCard = async (
  savedCardData: CardData,
  user: any,
  deleteCardFromCardsData: (cardId: string) => void,
  deletedFromLocation?: string
) => {
  let returnData: any = null;
  const savedCardId = savedCardData?.id ? savedCardData?.id : savedCardData?.id;
  if (!savedCardId) {
    return;
  }

  // remove from state first
  deleteCardFromCardsData(savedCardId);

  invalidateDeckCardRequests(savedCardData.deck_id);
  await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/cards/remove_saved_cards`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      user_id: user?.id,
      saved_card_ids_to_remove: [savedCardId],
    }),
  })
    .then(async (response) => {
      invalidateDeckCardRequests(savedCardData.deck_id);
      return response.json();
    })
    .then(async (response) => {
      if (response.status === "success") {
        // Get the question version being displayed for MCQ/True-False questions
        let trackingData: any = {
          savedCardId: savedCardId,
          pageUrl: savedCardData.page_url,
          question: savedCardData.question,
          answer: savedCardData.answer,
          index: savedCardData.card_index,
          cardType: savedCardData?.card_type,
          wasGeneratedWithNewPipeline: savedCardData?.was_generated_with_new_pipeline,
          question_generation_worker: savedCardData?.question_generation_worker,
          location: deletedFromLocation,
        };

        // Add rephrasing information for MCQ and True/False questions
        if (
          user?.id &&
          (savedCardData.card_type === CardType.RegularMultipleChoiceQuestion ||
            savedCardData.card_type === CardType.CaseScenarioMultipleChoiceQuestion ||
            savedCardData.card_type === CardType.USMLEStep1MCQ ||
            savedCardData.card_type === CardType.TrueOrFalseQuestion)
        ) {
          const questionVersionData = getQuestionVersionToDisplay(savedCardData, user);
          trackingData = {
            ...trackingData,
            displayedQuestion: questionVersionData.question,
            displayedAnswer: questionVersionData.answer,
            wasQuestionRephrased: questionVersionData.versionIndex !== -1,
            questionVersionIndex: questionVersionData.versionIndex,
          };
        }

        trackEvent("Card Deleted", trackingData, user);
        if (savedCardData.anki_card_id) {
          await removeAnkiCard(savedCardData.anki_card_id, user?.email_auth);
          savedCardData.anki_card_id = null;
        }
      } else {
        trackEvent("Error: Failed to delete saved card", savedCardData, user);
      }
      returnData = response;
    })
    .catch((error) => {
      trackEvent(
        "Error: Failed to delete saved card",
        {
          errorMessage: error.message,
        },
        user
      );
    });
  return returnData;
};

// The Worker removes each card of a request in parallel, with several Firestore calls per card: a few dozen per
// request keeps a large set's removal inside one Worker invocation's subrequest budget.
const MAXIMUM_SAVED_CARDS_TO_REMOVE_PER_REQUEST = 25;

/**
 * Removes the given saved cards on the server, in as few requests as the Worker allows. Resolves with the ids of the
 * cards whose request was confirmed, and whether that is all of them: a request that fails after earlier ones
 * succeeded leaves those removed, and the caller takes exactly them out of the review. It does not touch the page's
 * state, so a card whose removal was not confirmed stays where it was. Failures are reported to Sentry.
 */
export const removeSavedCardsAndConfirm = async (
  savedCardsToRemove: CardData[],
  user: UserData,
  removedFromLocation: string
): Promise<{ removedCardIds: string[]; didRemoveEveryCard: boolean }> => {
  const savedCardsWithIds = savedCardsToRemove.filter((savedCard) => !!savedCard?.id);
  if (savedCardsWithIds.length === 0) return { removedCardIds: [], didRemoveEveryCard: true };
  const deckIdsOfRemovedCards = new Set(savedCardsWithIds.map((savedCard) => savedCard.deck_id));
  const invalidateCachedCardsOfTheirDecks = () =>
    deckIdsOfRemovedCards.forEach((deckId) => invalidateDeckCardRequests(deckId));

  const removedCards: CardData[] = [];
  invalidateCachedCardsOfTheirDecks();
  try {
    for (
      let firstCardIndexOfRequest = 0;
      firstCardIndexOfRequest < savedCardsWithIds.length;
      firstCardIndexOfRequest += MAXIMUM_SAVED_CARDS_TO_REMOVE_PER_REQUEST
    ) {
      const savedCardsOfRequest = savedCardsWithIds.slice(
        firstCardIndexOfRequest,
        firstCardIndexOfRequest + MAXIMUM_SAVED_CARDS_TO_REMOVE_PER_REQUEST
      );
      const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/cards/remove_saved_cards`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user?.id,
          saved_card_ids_to_remove: savedCardsOfRequest.map((savedCard) => savedCard.id),
        }),
      });
      const responseBody = await response.json().catch(() => null);
      if (!response.ok || responseBody?.status !== "success") {
        trackError(new Error("Failed to remove saved cards"), {
          scope: "card-model",
          userId: user?.id,
          extra: {
            endpoint: "remove_saved_cards",
            httpStatus: response.status,
            responseStatus: responseBody?.status ?? null,
            numberOfCardsInRequest: savedCardsOfRequest.length,
            numberOfCardsAlreadyRemoved: firstCardIndexOfRequest,
            removedFromLocation,
          },
        });
        break;
      }
      removedCards.push(...savedCardsOfRequest);
    }
  } catch (error) {
    trackError(error, {
      scope: "card-model",
      userId: user?.id,
      extra: {
        endpoint: "remove_saved_cards",
        numberOfCards: savedCardsWithIds.length,
        numberOfCardsAlreadyRemoved: removedCards.length,
        removedFromLocation,
      },
    });
  } finally {
    invalidateCachedCardsOfTheirDecks();
  }

  // As for a single card: a removed card that was also exported to the learner's Anki goes there too. Best effort,
  // the removal itself is done.
  for (const removedCard of removedCards) {
    if (!removedCard.anki_card_id) continue;
    try {
      await removeAnkiCard(removedCard.anki_card_id, user?.email_auth);
    } catch (error) {
      trackError(error, {
        scope: "card-model",
        userId: user?.id,
        level: "warning",
        extra: { endpoint: "removeAnkiCard", removedFromLocation },
      });
    }
  }
  return {
    removedCardIds: removedCards.map((removedCard) => removedCard.id),
    didRemoveEveryCard: removedCards.length === savedCardsWithIds.length,
  };
};

export const deleteMultipleSavedCards = async (
  cardsArray: CardData[], // cards to delete
  user: UserData,
  updateCardFromCardsDataState: (card: CardData) => void,
  wasDeletedFromConcept: boolean = false // if questions were deleted because a concept was deleted
) => {
  let returnData: any = null;
  new Set(cardsArray.map(card => card.deck_id)).forEach(deckId => invalidateDeckCardRequests(deckId));
  await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/cards/remove_saved_cards`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      user_id: user?.id,
      saved_card_ids_to_remove: cardsArray?.map((card) => card?.id),
    }),
  })
    .then(async (response) => {
      new Set(cardsArray.map(card => card.deck_id)).forEach(deckId => invalidateDeckCardRequests(deckId));
      return response.json();
    })
    .then(async (response) => {
      if (response.status === "success") {
        for (let card of cardsArray) {
          if (card.anki_card_id) {
            await removeAnkiCard(card.anki_card_id, user?.email_auth);
            card.anki_card_id = null;
          }

          card.is_saved = false;
          card.is_deleted = true;
          updateCardFromCardsDataState(card);
        }
        const questionGenerationWorkersForDeletedCards = Array.from(
          new Set(cardsArray.map((card) => card?.question_generation_worker).filter(Boolean))
        );
        const questionGenerationWorkerForDeletedCards =
          questionGenerationWorkersForDeletedCards.length === 1 ? questionGenerationWorkersForDeletedCards[0] : "mixed";

        trackEvent(
          "Deleted Multiple Cards",
          {
            cardIds: cardsArray.map((card) => card.id),
            numberOfCardsDeleted: cardsArray.length,
            wasDeletedFromConcept: wasDeletedFromConcept,
            question_generation_worker: questionGenerationWorkerForDeletedCards,
          },
          user
        );
      } else {
        trackEvent("Error: Failed to Flashcards Deleted", { cardsArray }, user);
      }
      returnData = response;
    })
    .catch((error) => {
      console.error("Error to Flashcard Deleted:", error);
      trackEvent(
        "Error: Failed to Flashcard Deleted",
        {
          errorMessage: error.message,
        },
        user
      );
    });
  return returnData;
};

export const addCustomCardToDeck = async (
  page_url: string,
  answer: string,
  question: string,
  deck_id: string,
  deck_name: string,
  user: UserData,
  question_side_image_url: string | null,
  answer_side_image_url: string,
  card_type: string | null,
  distractor_answers_for_multiple_choice_question: string[],
  caseScenario: string,
  keyConceptName: string
) => {
  let createdCard: any = null;
  invalidateDeckCardRequests(deck_id);
  await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/cards/add_custom_card_to_deck`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      user_id: user.id,
      deck_id: deck_id,
      deck_name: deck_name,
      question: question,
      page_url: page_url,
      answer: answer,
      question_side_image_url: question_side_image_url,
      answer_side_image_url: answer_side_image_url,
      card_type: card_type,
      distractor_answers_for_multiple_choice_question: distractor_answers_for_multiple_choice_question,
      case_scenario_details: caseScenario,
      card_key_concept_name: keyConceptName,
    }),
  })
    .then(async (response) => {
      invalidateDeckCardRequests(deck_id);
      return response.json();
    })
    .then(async (response) => {
      if (response.status === "success") {
        trackEvent(
          "Added a custom card to a deck",
          {
            question: question,
            answer: answer,
            deck_name: deck_name,
            page_url: page_url,
            deck_id: deck_id,
            card_type: card_type,
            distractor_answers_for_multiple_choice_question: distractor_answers_for_multiple_choice_question,
          },
          user
        );
        createdCard = response.object;
      } else {
        trackEvent(
          "Error: Failed to add a custom card to a deck",
          {
            errorMessage: response,
            question: question,
            answer: answer,
            deck_name: deck_name,
            page_url: page_url,
            deck_id: deck_id,
            card_type: card_type,
            distractor_answers_for_multiple_choice_question: distractor_answers_for_multiple_choice_question,
          },
          user
        );
      }
    })
    .catch((error) => {
      console.error("Error: Failed to add a custom card to a deck:", error);
      trackEvent(
        "Error:  Failed to add a custom card to a deck",
        {
          errorMessage: error.message,
          question: question,
          answer: answer,
          deck_name: deck_name,
          page_url: page_url,
          deck_id: deck_id,
          card_type: card_type,
          distractor_answers_for_multiple_choice_question: distractor_answers_for_multiple_choice_question,
        },
        user
      );
    });
  return createdCard;
};

export const copyCardsFromUserToCardsSaved = async () => {
  const usersCollectionRef = collection(db, `${USERS_COLLECTION_NAME}`);
  const usersCollection = await getDocs(usersCollectionRef);
  const users = arrayElementLooper(usersCollection.docs);
  for (let user of users) {
    let flashCardsCollectionRef = collection(db, `${USERS_COLLECTION_NAME}/${user.id}/${CARDS_SAVED_COLLECTION_NAME}`);
    const flashCardsCollection = await getDocs(flashCardsCollectionRef);
    const flashCards = arrayElementLooper(flashCardsCollection.docs);
    for (let flashCard of flashCards) {
      flashCard.user_id = user.id;
      flashCard.did_save_to_anki = flashCard.did_save_to_anki == null ? true : flashCard.did_save_to_anki;
      setDoc(doc(db, `${CARDS_SAVED_COLLECTION_NAME}`, flashCard.id), flashCard, { merge: true });
    }
  }
};

export const getFlashcardsByDeckId = async (deckId: any) => {
  const flashCardsCollectionRef = collection(db, `${DECK_COLLECTION_NAME}/${deckId}/${CARDS_SAVED_COLLECTION_NAME}`);
  const flashCardsQueryCollectionRef = query(flashCardsCollectionRef, orderBy("date_saved", "desc"));
  const flashCardsCollection = await getDocs(flashCardsQueryCollectionRef);
  const flashCards = arrayElementLooper(flashCardsCollection.docs);
  return flashCards;
};

export const getSavedCardDataByCardId = async (flashcardId: string) => {
  const flashcardDoc = doc(db, CARDS_SAVED_COLLECTION_NAME, flashcardId);
  let returnData: any = null;

  await getDoc(flashcardDoc)
    .then((snapshot: any) => {
      if (snapshot.exists()) {
        returnData = snapshot.data();
        returnData.id = snapshot.id;
      } else {
        // console.log("Flashcard with that id does not exist");
      }
    })
    .catch((err) => {
      // console.log("Flashcard with that id does not exist: ", err);
    });
  return returnData;
};

export const getSavedCardsDataByCardIds = async (flashcardIds: string[]) => {
  const flashcardDocs = flashcardIds.map((id) => doc(db, CARDS_SAVED_COLLECTION_NAME, id));
  let returnData: any[] = [];

  await Promise.all(flashcardDocs.map((flashcardDoc) => getDoc(flashcardDoc)))
    .then((snapshots) => {
      snapshots.forEach((snapshot: any) => {
        if (snapshot.exists()) {
          let data = snapshot.data();
          data.id = snapshot.id;
          returnData.push(data);
        }
      });
    })
    .catch((err) => {
      console.error("Error getting flashcards: ", err);
    });
  return returnData;
};

export const getCardsByDeckId = async (deck_id: string, user: UserData) => {
  let savedCardsArray: any = [];
  // This route is served natively by the Cloudflare worker. It previously
  // pointed at JUNGLE_BACKEND_PROD_DOMAIN, where the Python implementation no
  // longer exists — every call 404'd and mastery-progress init silently ran
  // on an empty card list (fixed 2026-06-11).
  await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/cards/get_cards_for_deck`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      user_id: user?.id,
      deck_id: deck_id,
    }),
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`get_cards_for_deck failed with HTTP ${response.status}`);
      }
      return response.json();
    })
    .then(async (response) => {
      if (response.status === "success") {
        savedCardsArray = response.cards;
      } else {
        trackError(new Error("Failed to get cards for deck"), {
          scope: "card-model",
          userId: user?.id,
          extra: { deck_id, responseStatus: response.status },
        });
      }
    })
    .catch((error) => {
      trackError(error, {
        scope: "card-model",
        userId: user?.id,
        extra: { deck_id, endpoint: "get_cards_for_deck" },
      });
    });
  // when the user is not the owner of the deck, we need to remove the answers and ratings
  if (savedCardsArray[0]?.user_id !== user?.id) {
    savedCardsArray = savedCardsArray.map((card) => {
      card.answers_from_user_and_ai_feedback = [];
      card.knowledge_understanding_ratings = [];
      return card;
    });
  }
  return savedCardsArray;
};

export const doesUserHaveSavedFlashcardsByUserId = async (userId: string) => {
  const flashCardsCollectionRef = collection(db, `${USERS_COLLECTION_NAME}/${userId}/${CARDS_SAVED_COLLECTION_NAME}`);
  const flashCardsCollection = await getDocs(flashCardsCollectionRef);
  return flashCardsCollection.docs.length > 0 ? true : false;
};

export const generateAnswerForQuestionToDebugPrompt = async (
  question: string,
  card_type: CardType,
  card_generation_language: string,
  relevant_context_for_question: string,
  usual_misconceptions: string[] | null,
  answer: string,
  taxonomy_level: string,
  user: UserData
) => {
  await fetch(`${JUNGLE_BACKEND_PROD_DOMAIN}/cards/generate_answer_for_question_debugging`, {
    method: "POST",
    body: JSON.stringify({
      user_id: user?.id,
      question: question,
      answer: answer,
      card_type: card_type,
      card_generation_language: card_generation_language,
      relevant_context_for_question: relevant_context_for_question,
      usual_misconceptions: usual_misconceptions,
      taxonomy_level: taxonomy_level,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const calculateNextFrfsReviewForCard = async (card: CardData, rate: number, user: UserData) => {
  // If required identifiers are missing, do not attempt to update anything.
  if (!card || (!card.id && !card.card_id && !card.flashcard_generated_id) || !user?.id) {
    return;
  }

  // Determine the Firestore document id for this card.
  const cardDocId = card.id || card.card_id || card.flashcard_generated_id;

  // Field names are stored per user so that each user has independent FRFS data.
  const userId = user.id as string;
  const learningStepFieldName = `frfs_learning_step_by_user_${userId}`;
  const nextReviewAtFieldName = `frfs_next_review_at_by_user_${userId}`;

  // Default learning step is 0 if none has been stored yet.
  const currentLearningStepFromCard = (card as any)[learningStepFieldName];
  let currentLearningStep =
    typeof currentLearningStepFromCard === "number" && !isNaN(currentLearningStepFromCard)
      ? currentLearningStepFromCard
      : 0;

  // Define FRFS intervals for each learning step.
  // Steps (in minutes): [10 min, 1 day, 3 days, 7 days, 14 days, 30 days].
  const learningStepIntervalsInMinutes: number[] = [
    10, // 10 minutes
    60 * 24, // 1 day
    3 * 24 * 60, // 3 days
    7 * 24 * 60, // 7 days
    14 * 24 * 60, // 14 days
    30 * 24 * 60, // 30 days
  ];
  const maxLearningStepIndex = learningStepIntervalsInMinutes.length - 1;

  // Helper to add minutes to the current timestamp.
  const now = new Date();
  const addMinutesToNow = (minutes: number): Date => new Date(now.getTime() + minutes * 60 * 1000);

  // Map the numeric rate to one of the FRFS response categories:
  // "Forgot", "Good", or "Easy".
  let responseCategory: "Forgot" | "Good" | "Easy";

  const cardType = card.card_type;

  // Free response style questions: map according to the Open question rules.
  if (
    cardType === CardType.RegularFreeResponseQuestion ||
    cardType === CardType.CaseScenarioFreeResponseQuestion ||
    cardType === CardType.OpenEndedQuestion
  ) {
    if (rate === 3) {
      responseCategory = "Easy";
    } else if (rate === 2) {
      responseCategory = "Good";
    } else {
      responseCategory = "Forgot";
    }
  } else if (
    cardType === CardType.RegularMultipleChoiceQuestion ||
    cardType === CardType.CaseScenarioMultipleChoiceQuestion ||
    cardType === CardType.USMLEStep1MCQ ||
    cardType === CardType.ImageOcclusion ||
    cardType === CardType.TrueOrFalseQuestion
  ) {
    // Multiple choice & true/false questions only distinguish between correct and incorrect.
    // MCQ: correct => 3 (Good); incorrect => 0 (Forgot)
    // True/False: correct => 2 (Good); incorrect => 1 (Forgot)
    const isCorrectMcq = cardType !== CardType.TrueOrFalseQuestion && rate === 3;
    const isCorrectTrueOrFalse = cardType === CardType.TrueOrFalseQuestion && rate === 2;

    if (isCorrectMcq || isCorrectTrueOrFalse) {
      responseCategory = "Good";
    } else {
      responseCategory = "Forgot";
    }
  } else {
    // Fallback mapping if card type is unknown or new.
    if (rate >= 3) {
      responseCategory = "Easy";
    } else if (rate === 2) {
      responseCategory = "Good";
    } else {
      responseCategory = "Forgot";
    }
  }

  let newLearningStep = currentLearningStep;
  let nextReviewAt: Date;

  // Apply FRFS scheduling rules based on the response category.
  if (responseCategory === "Forgot") {
    // Forgot: reset to step 0 and review again in 10 minutes.
    newLearningStep = 0;
    nextReviewAt = addMinutesToNow(learningStepIntervalsInMinutes[0]);
  } else {
    if (responseCategory === "Good") {
      // Good: move forward one step.
      newLearningStep = currentLearningStep + 1;
    } else if (responseCategory === "Easy") {
      // Easy: move forward two steps.
      newLearningStep = currentLearningStep + 2;
    }

    // Do not allow the learning step to exceed the maximum defined interval index.
    if (newLearningStep > maxLearningStepIndex) {
      newLearningStep = maxLearningStepIndex;
    }

    const intervalMinutes = learningStepIntervalsInMinutes[newLearningStep];
    nextReviewAt = addMinutesToNow(intervalMinutes);
  }

  // Prepare the fields to update for this specific user.
  const fieldsToUpdate: any = {};
  fieldsToUpdate[learningStepFieldName] = newLearningStep;
  fieldsToUpdate[nextReviewAtFieldName] = nextReviewAt;

  // Persist the updated learning step and next review time for this user.
  await updateCardById(cardDocId, fieldsToUpdate);

  // Also update the in-memory card object so the caller has the latest values.
  (card as any)[learningStepFieldName] = newLearningStep;
  (card as any)[nextReviewAtFieldName] = nextReviewAt;
};

// ******** SCRIPTS ******** //
// ******** SCRIPTS ******** //

// export const updateAllSavedCards = async () => {
//   console.log("starting...");

//   const savedsCardsCollectionRef = collection(db, `${CARDS_SAVED_COLLECTION_NAME}`);
//   const savedCardsDocs = await getDocs(savedsCardsCollectionRef);
//   console.log("savedCardsDocs", savedCardsDocs);
//   // const savedCards = arrayElementLooper(savedCardsDocs.docs);
//   // let index = 0;
//   // for (let card of savedCards) {
//   //   console.log("card", card);
//   //   if (card.user_id == "f3OzifYzLDb5VPjHWUW4WdSsZoy1") {
//   //     const generatedCardId = card.id ? card.id : card.flashcard_generated_id;
//   //     console.log("generatedCardId", generatedCardId);
//   //     console.log("card_id", card.doc_id);
//   //     // await updateCardById(card.doc_id,{generated_card_id:generatedCardId, card_id:card.doc_id})
//   //   }
//   //   // console.log(card);
//   //   // console.log(user.id);
//   //   // index++;
//   // }

//   //   const savedsCardsCollectionRef = collection(db, `${CARDS_SAVED_COLLECTION_NAME}`);

//   // for (let docId of docIds) {
//   //   const docRef = doc(savedsCardsCollectionRef, docId);
//   //   const docSnap = await getDoc(docRef);

//   //   if (docSnap.exists()) {
//   //     const card = docSnap.data();
//   //     console.log("card", card);
//   //     // ... your other logic ...
//   //   }
//   // }
//   //   console.log("...finish");
// };
